import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  CircleHelp,
  ClipboardCopy,
  Clock3,
  FileText,
  Languages,
  Lightbulb,
  Loader2,
  LockKeyhole,
  MessageSquareText,
  RefreshCw,
  ShieldCheck,
  X,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import incidentCopilotMarkWhite from '@/assets/incident-copilot/foresight-mark-white.png';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const COPILOT_ACTIONS = [
  {
    id: 'current_summary',
    title: 'Current situation',
    description: 'Summarize the latest operational picture.',
    icon: FileText,
  },
  {
    id: 'overdue_actions',
    title: 'Overdue actions',
    description: 'Identify late or stalled assigned work.',
    icon: Clock3,
  },
  {
    id: 'conflict_detection',
    title: 'Conflicting updates',
    description: 'Find material contradictions in updates.',
    icon: AlertTriangle,
  },
  {
    id: 'unconfirmed_items',
    title: 'Unconfirmed items',
    description: 'Surface claims and decisions still awaiting confirmation.',
    icon: CircleHelp,
  },
  {
    id: 'next_step',
    title: 'Suggested next step',
    description: 'Recommend the next operational priority.',
    icon: Lightbulb,
  },
  {
    id: 'leadership_update',
    title: 'Leadership update',
    description: 'Draft a concise command-level briefing.',
    icon: MessageSquareText,
  },
];

const responsePayload = (response) => {
  const candidates = [response?.data?.data, response?.data, response];
  return candidates.find((candidate) => candidate && typeof candidate === 'object' && (
    typeof candidate.result === 'string'
    || typeof candidate.answer === 'string'
    || typeof candidate.output === 'string'
  )) || null;
};

const responseError = (error, fallback) => (
  error?.response?.data?.detail
  || error?.response?.data?.message
  || error?.message
  || fallback
);

const evidenceText = (evidence) => {
  if (typeof evidence === 'string') return evidence;
  if (!evidence || typeof evidence !== 'object') return String(evidence || '');
  const label = evidence.label || evidence.title || evidence.source || evidence.type;
  const value = evidence.value || evidence.detail || evidence.summary || evidence.reference || evidence.claim || evidence.excerpt;
  if (label && value) return `${label}: ${value}`;
  if (label) return String(label);
  try {
    return JSON.stringify(evidence);
  } catch (_error) {
    return 'Incident evidence';
  }
};

export const isIncidentCopilotEligible = (incident, scenarioMode = false) => Boolean(
  incident
  && !scenarioMode
  && incident?.is_exercise !== true
  && incident?.is_scenario !== true
  && incident?.is_planned_event !== true
);

export const CopilotMark = ({ placement = 'header', className = '' }) => {
  if (placement === 'empty') {
    return (
      <span
        aria-hidden="true"
        data-testid="incident-copilot-mark"
        data-placement="empty"
        className={`relative inline-flex h-[104px] w-[104px] flex-none items-center justify-center ${className}`}
      >
        <span className="absolute inset-0 rounded-full border border-white/15" />
        <span className="absolute inset-[17px] rounded-full border border-white/10" />
        <img
          src={incidentCopilotMarkWhite}
          alt=""
          data-testid="incident-copilot-logo"
          className="relative z-10 h-[58px] w-[58px] object-contain drop-shadow-[0_14px_28px_rgba(147,180,255,0.32)]"
        />
      </span>
    );
  }

  if (placement === 'header-control') {
    return (
      <span
        aria-hidden="true"
        data-testid="incident-copilot-mark"
        data-placement="header-control"
        className={`inline-flex h-5 w-5 flex-none items-center justify-center ${className}`}
      >
        <img
          src={incidentCopilotMarkWhite}
          alt=""
          data-testid="incident-copilot-logo"
          className="h-[18px] w-[18px] object-contain"
        />
      </span>
    );
  }

  const launcher = placement === 'launcher';
  return (
    <span
      aria-hidden="true"
      data-testid="incident-copilot-mark"
      data-placement={placement}
      className={`inline-flex flex-none items-center justify-center border border-white/20 bg-white/10 ${launcher ? 'h-[34px] w-[34px] rounded-[10px]' : 'h-[46px] w-[46px] rounded-[13px]'} ${className}`}
    >
      <img
        src={incidentCopilotMarkWhite}
        alt=""
        data-testid="incident-copilot-logo"
        className={`${launcher ? 'h-5 w-5' : 'h-[26px] w-[26px]'} object-contain`}
      />
    </span>
  );
};

export const IncidentCopilotButton = ({ onClick, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="open-incident-copilot"
    aria-label="Open Incident Copilot"
    className={`inline-flex flex-shrink-0 items-center text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${compact
      ? 'h-9 gap-2 rounded-lg border border-white/15 bg-white/10 px-3 text-[11px] hover:bg-white/20'
      : 'gap-3 rounded-[14px] bg-[linear-gradient(90deg,#0f172a_0%,#1e3a8a_100%)] py-3 pl-3.5 pr-5 text-base shadow-[0_16px_34px_-18px_rgba(9,16,38,0.7)] hover:brightness-110 focus-visible:ring-offset-2'}`}
    title="Open Incident Copilot"
  >
    <CopilotMark placement={compact ? 'header-control' : 'launcher'} />
    <span className={`font-semibold tracking-[-0.15px] [font-family:'Poppins',sans-serif] ${compact ? 'uppercase tracking-[0.04em]' : ''}`}>Incident Copilot</span>
  </button>
);

const IncidentCopilotPanel = ({ incident, onClose }) => {
  const [language, setLanguage] = useState('English');
  const [selectedAction, setSelectedAction] = useState('');
  const [result, setResult] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [generatedAt, setGeneratedAt] = useState('');
  const [resultLanguage, setResultLanguage] = useState('English');
  const [reviewKind, setReviewKind] = useState(
    incident?.is_exercise === true || incident?.is_scenario === true ? 'aar' : 'pir'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const requestRef = useRef(0);
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const isArabic = language === 'Arabic';
  const isResultArabic = resultLanguage === 'Arabic';
  const activeAction = COPILOT_ACTIONS.find((action) => action.id === selectedAction);

  useEffect(() => {
    requestRef.current += 1;
    setSelectedAction('');
    setResult('');
    setEvidence([]);
    setGeneratedAt('');
    setError('');
    setLoading(false);
    setCopied(false);
    setReviewKind(incident?.is_exercise === true || incident?.is_scenario === true ? 'aar' : 'pir');
    return () => {
      requestRef.current += 1;
    };
  }, [incident?.id, incident?.is_exercise, incident?.is_scenario]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, [onClose]);

  const runAction = async (actionId) => {
    if (!incident?.id) return;
    const requestId = ++requestRef.current;
    const requestedLanguage = language;
    setSelectedAction(actionId);
    setLoading(true);
    setError('');
    setResult('');
    setEvidence([]);
    setCopied(false);
    try {
      const response = await CommanServices.runIncidentCopilot(incident.id, {
        action: actionId,
        language: requestedLanguage,
      });
      if (requestId !== requestRef.current) return;
      const payload = responsePayload(response);
      const output = String(payload?.result || payload?.answer || payload?.output || '').trim();
      if (response?.status === false || !output) {
        throw new Error(response?.data?.detail || response?.message || 'Incident Copilot could not produce a grounded answer.');
      }
      setResult(output);
      setResultLanguage(requestedLanguage);
      setEvidence(Array.isArray(payload?.evidence) ? payload.evidence : []);
      setGeneratedAt(payload?.generatedAt || payload?.generated_at || new Date().toISOString());
      const kind = String(payload?.reviewKind || payload?.review_kind || '').toLowerCase();
      if (kind === 'pir' || kind === 'aar') setReviewKind(kind);
    } catch (requestError) {
      if (requestId === requestRef.current) {
        setError(responseError(requestError, 'Incident Copilot is temporarily unavailable.'));
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_copyError) {
      setCopied(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-950/70 p-2 backdrop-blur-sm sm:p-5"
      data-testid="incident-copilot-panel"
      data-modal-overlay="true"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="incident-copilot-title"
        aria-describedby="incident-copilot-description"
        data-testid="incident-copilot-dialog"
        className="flex h-[min(700px,calc(100vh-16px))] w-full max-w-[1180px] flex-col overflow-hidden rounded-[22px] bg-[linear-gradient(90deg,#0f172a_0%,#1e3a8a_100%)] text-white shadow-[0_40px_90px_-44px_rgba(9,16,38,0.75)] sm:h-[min(700px,calc(100vh-40px))]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex flex-none items-center gap-3 px-4 py-3.5 sm:gap-4 sm:px-5 sm:py-4">
          <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-[18px]">
            <CopilotMark placement="header" />
            <div className="min-w-0">
              <h2 id="incident-copilot-title" className="truncate text-[17px] font-semibold tracking-[-0.2px] text-white [font-family:'Poppins',sans-serif] sm:text-[19px]">Incident Copilot</h2>
              <p id="incident-copilot-description" className="mt-0.5 truncate text-[11px] text-white/70 sm:mt-[3px] sm:text-[13px]">{incident?.id} · grounded in the authoritative incident record</p>
            </div>
          </div>
          <span className="hidden items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[1px] text-white sm:inline-flex">
            <span className="h-[7px] w-[7px] rounded-full bg-emerald-400" />
            {reviewKind === 'aar' ? 'Exercise context' : 'Real incident context'}
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close Incident Copilot"
            className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[11px] border border-white/20 bg-white/10 text-white/75 transition hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
            data-testid="close-incident-copilot"
          >
            <X className="h-[17px] w-[17px]" strokeWidth={2} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside className="no-scrollbar flex max-h-[44%] flex-none flex-col gap-3 overflow-y-auto px-4 pb-3 pt-1 md:max-h-none md:w-[320px] md:overflow-hidden md:px-[18px] md:pb-4">
            <div>
              <label htmlFor="copilot-language" className="flex items-center gap-[9px] text-[11px] font-semibold uppercase tracking-[1.4px] text-white/60">
                <Languages className="h-[15px] w-[15px]" strokeWidth={1.8} /> Response language
              </label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger
                  id="copilot-language"
                  className="mt-2 h-10 rounded-[11px] border-white/20 bg-white/[0.09] px-3 py-2 text-[13px] font-medium text-white shadow-none transition hover:bg-white/[0.15] focus:ring-1 focus:ring-white/40 [&>svg]:text-white/70"
                  data-testid="copilot-language-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[2100] border-white/15 bg-slate-950 text-white shadow-2xl">
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Arabic">Arabic (العربية)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-[11px] font-semibold uppercase tracking-[1.4px] text-white/60">Choose an operational question</p>
            <div className="flex flex-col gap-1.5">
              {COPILOT_ACTIONS.map((action, index) => {
                const Icon = action.icon;
                const active = selectedAction === action.id;
                const visuallyActive = active || (!selectedAction && index === 0);
                return (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => runAction(action.id)}
                    aria-pressed={active}
                    data-testid={`copilot-action-${action.id}`}
                    className={`group flex w-full items-center gap-2.5 rounded-[11px] border px-3 py-2.5 text-left text-white transition ${visuallyActive ? 'border-white/30 bg-white/[0.14]' : 'border-white/[0.14] bg-white/[0.07] hover:bg-white/[0.13]'}`}
                  >
                    <span className={`flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] ${visuallyActive ? 'bg-white/[0.16]' : 'bg-white/[0.12]'}`}>
                      {active && loading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Icon className="h-4 w-4" strokeWidth={1.8} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold leading-[18px] text-white">{action.title}</span>
                      <span className="mt-0.5 block truncate text-[11px] leading-4 text-white/70">{action.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col px-4 pb-4 pt-1 sm:px-5 sm:pb-5" dir={result ? (isResultArabic ? 'rtl' : 'ltr') : (isArabic ? 'rtl' : 'ltr')} aria-busy={loading} aria-live="polite">
            <div className="flex flex-none items-end justify-between gap-4 pb-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[1.4px] text-white/60">Copilot result</p>
                <h3 className="mt-1 text-[17px] font-semibold text-white [font-family:'Poppins',sans-serif]">{activeAction?.title || 'Select a question to begin'}</h3>
              </div>
              <button
                type="button"
                onClick={copyResult}
                disabled={!result}
                className="inline-flex items-center gap-[9px] rounded-[11px] border border-white/20 bg-white/[0.09] px-4 py-2.5 text-[13px] font-medium text-white/85 transition hover:bg-white/[0.16] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="copy-copilot-result"
              >
                {copied ? <Check className="h-[15px] w-[15px] text-emerald-300" strokeWidth={1.8} /> : <ClipboardCopy className="h-[15px] w-[15px]" strokeWidth={1.8} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
              {!selectedAction && !loading && (
                <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-4 rounded-[16px] border border-dashed border-white/[0.18] bg-white/[0.04] px-6 py-8 text-center sm:px-8" dir="ltr">
                  <CopilotMark placement="empty" />
                  <div>
                    <h3 className="text-[19px] font-semibold text-white [font-family:'Poppins',sans-serif]">Operational intelligence on demand</h3>
                    <p className="mx-auto mt-2 max-w-[520px] text-[13px] leading-[1.6] text-white/70">Every answer is rebuilt from the latest authorized incident evidence. Copilot never executes actions or sends messages on its own.</p>
                  </div>
                  <span className="inline-flex items-center gap-[9px] rounded-full border border-white/[0.16] bg-white/[0.08] px-[15px] py-[9px] text-[12px] text-white/75 sm:text-[12.5px]">
                    <LockKeyhole className="h-3.5 w-3.5" strokeWidth={2} />
                    Read-only · answers are traceable to source records
                  </span>
                </div>
              )}

              {loading && (
                <div className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-[16px] border border-white/[0.14] bg-white/[0.05] px-8 text-center" aria-live="polite" data-testid="copilot-loading">
                  <Loader2 className="h-9 w-9 animate-spin text-white" strokeWidth={1.8} />
                  <p className="mt-5 text-[15px] font-semibold text-white">Reviewing the latest {reviewKind === 'aar' ? 'exercise' : 'incident'} evidence…</p>
                  <p className="mt-1.5 text-[13px] text-white/65">Timeline, tasks, verifications, updates and authorized communications</p>
                </div>
              )}

              {error && !loading && (
                <div className="rounded-[16px] border border-red-200/30 bg-red-400/10 p-5 text-[14px] text-red-50" role="alert" data-testid="copilot-error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                    <div className="flex-1">
                      <p className="font-semibold">Copilot could not complete this request</p>
                      <p className="mt-1 text-[12px] text-red-50/80">{error}</p>
                    </div>
                    <button type="button" onClick={() => runAction(selectedAction)} aria-label={`Retry ${activeAction?.title || 'Copilot request'}`} className="inline-flex items-center gap-1 text-[12px] font-bold hover:underline">
                      <RefreshCw className="h-3.5 w-3.5" /> Retry
                    </button>
                  </div>
                </div>
              )}

              {result && !loading && (
                <div data-testid="copilot-result">
                  <div className="rounded-[16px] border border-white/[0.14] bg-white/[0.07] p-5 sm:p-7">
                    <div className="prose prose-sm prose-invert max-w-none text-white/80 prose-headings:font-semibold prose-headings:text-white prose-a:text-blue-200 prose-strong:text-white prose-li:text-white/80">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{result}</ReactMarkdown>
                    </div>
                  </div>
                  {evidence.length > 0 && (
                    <div className="mt-4 rounded-[13px] border border-white/[0.14] bg-white/[0.07] p-4" data-testid="copilot-evidence">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[1.4px] text-white/70">
                        <ShieldCheck className="h-3.5 w-3.5" /> Evidence references
                      </p>
                      <ul className="mt-2 space-y-1.5 text-[12px] text-white/75">
                        {evidence.map((item, index) => <li key={`${evidenceText(item)}-${index}`}>• {evidenceText(item)}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/55">
                    <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Advisory only · no action was executed or message sent</span>
                    {generatedAt && <time dateTime={generatedAt}>Generated {new Date(generatedAt).toLocaleString()}</time>}
                  </div>
                </div>
              )}
            </div>

            {result && selectedAction !== 'leadership_update' && !loading && (
              <div className="flex-none pt-3" dir="ltr">
                <button type="button" onClick={() => runAction('next_step')} className="inline-flex items-center gap-1.5 rounded-[11px] border border-white/[0.16] bg-white/[0.08] px-3 py-2 text-[12px] font-semibold text-white/80 transition hover:bg-white/[0.14] hover:text-white">
                  Ask for the suggested next step <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default IncidentCopilotPanel;
